'use client'

import Image from 'next/image'
import { useState, useEffect, useCallback } from 'react'

/**
 * Displays a randomly selected product image from an array of images.
 * Randomization happens on the client side so each page load shows a different image.
 */
const RandomProductImage = ({
    images = [],
    fallbackSrc = '/images/featured-product-placeholder.jpg',
    className = 'w-[120px] h-[120px] object-contain',
    alt = 'Featured product',
    width = 120,
    height = 120,
}) => {
    const [selectedImage, setSelectedImage] = useState(null)

    const randomize = useCallback((items) => {
        const randomIndex = Math.floor(Math.random() * items.length)
        return items[randomIndex]
    }, [])

    useEffect(() => {
        if (images && images.length > 0) {
            setSelectedImage(randomize(images))
        }
    }, [images, randomize])

    // Show nothing until client-side selection happens (prevents hydration mismatch)
    if ( ! selectedImage && images.length > 0) {
        return (
            <div className={className} style={{ width, height }} />
        )
    }

    // No images available - show fallback
    if ( ! selectedImage) {
        return (
            <Image
                className={className}
                src={fallbackSrc}
                alt={alt}
                width={width}
                height={height}
            />
        )
    }

    const imageSrc = `${process.env.NEXT_PUBLIC_RESOURCE_URL}/${selectedImage.file_name}`
    return (
        <Image
            className={className}
            src={imageSrc}
            overrideSrc={imageSrc}
            alt={selectedImage.alt || alt}
            width={selectedImage.width || width}
            height={selectedImage.height || height}
        />
    )
}

export default RandomProductImage
